import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../services/authService";
import {
  APP_ROUTES,
  clearReturnTarget,
  consumeReturnTarget,
  peekReturnTarget,
} from "../routing/appRoutes";
import { AlertTriangleIcon } from "./SvgIcons";

interface AuthCallbackProps {
  onLogin: (userId: string, userProfile: any) => void;
}

type CallbackResult =
  | { userProfile: any; error: null }
  | { userProfile: null; error: string };

const AuthCallback: React.FC<AuthCallbackProps> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onLoginRef = useRef(onLogin);
  const navigateRef = useRef(navigate);
  const callbackPromiseRef = useRef<Promise<CallbackResult> | null>(null);
  onLoginRef.current = onLogin;
  navigateRef.current = navigate;

  useEffect(() => {
    let isMounted = true;

    const restoreProfileWithRetry = async () => {
      let lastError: unknown;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const userProfile = await AuthService.getCurrentUserProfile();

          if (userProfile) {
            return userProfile;
          }
        } catch (err) {
          lastError = err;
        }

        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      if (lastError) {
        throw lastError;
      }

      return null;
    };

    const handleCallback = async (): Promise<CallbackResult> => {
      try {
        let userProfile;
        try {
          userProfile = await AuthService.handleAuthCallback();
        } catch (callbackError) {
          userProfile = await restoreProfileWithRetry();

          if (!userProfile) {
            throw callbackError;
          }
        }

        if (userProfile) {
          return { userProfile, error: null };
        }

        return { userProfile: null, error: "Failed to authenticate" };
      } catch (err) {
        console.error("❌ Auth callback error:", err);
        const userProfile = await restoreProfileWithRetry().catch(() => null);

        if (userProfile) {
          return { userProfile, error: null };
        }

        return {
          userProfile: null,
          error: err instanceof Error ? err.message : "Authentication failed",
        };
      }
    };

    setIsLoading(true);
    setError(null);
    if (!callbackPromiseRef.current) {
      callbackPromiseRef.current = handleCallback();
    }

    void callbackPromiseRef.current
      .then((result) => {
        if (!isMounted) return;

        if (!result.userProfile) {
          clearReturnTarget();
          setError(result.error);
          return;
        }

        const userProfile = result.userProfile;
        if (
          !userProfile.level ||
          !userProfile.interests ||
          userProfile.interests.length === 0
        ) {
          localStorage.setItem(
            "onboardingUserProfile",
            JSON.stringify(userProfile),
          );
          navigateRef.current(APP_ROUTES.onboarding, { replace: true });
          return;
        }

        const destination = peekReturnTarget();
        onLoginRef.current(userProfile.id, userProfile);
        navigateRef.current(destination, { replace: true });
        // A login-state rerender can mount App's authenticated callback
        // safeguard before this navigation commits. Let that safeguard capture
        // the same validated target before the single-use value is cleared.
        window.setTimeout(() => {
          consumeReturnTarget();
        }, 0);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Completing Login...
          </h2>
          <p className="text-gray-600">
            Please wait while we complete your authentication.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-red-500 mb-4">
            <AlertTriangleIcon className="mx-auto text-4xl" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Authentication Failed
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.href = "/"}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
